import { Bot, Calendar, Sparkles, RefreshCw, BarChart3, Presentation } from 'lucide-react';
import { MonthlyReport } from '../types';
import { format } from 'date-fns';
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import DebtAdvisor from './DebtAdvisor';

interface AIDashboardProps {
  reports: MonthlyReport[];
  userId: string;
  householdId: string;
}

export default function AIDashboard({ reports, userId, householdId }: AIDashboardProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'monthly' | 'advisor'>('monthly');

  const handleManualTrigger = async () => {
    setIsGenerating(true);
    try {
      const triggerStrategy = httpsCallable(functions, 'triggerAIStrategy');
      await triggerStrategy();
      // The report will be generated and Firestore snapshot listener in Dashboard will pick it up automatically
    } catch (err) {
      console.error('Failed to trigger strategy:', err);
      alert('Failed to generate strategy. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50 shrink-0">
        <div className="flex bg-slate-200/50 p-1 rounded-lg max-w-sm mb-4">
          <button
            onClick={() => setActiveTab('monthly')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Presentation className="w-4 h-4" /> Monthly Strategist
          </button>
          <button
            onClick={() => setActiveTab('advisor')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'advisor' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <BarChart3 className="w-4 h-4" /> Debt & Wealth Advisor
          </button>
        </div>

        {activeTab === 'monthly' && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                <Bot className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-slate-900">Monthly Strategist AI</h2>
                <p className="text-sm text-slate-500">On-demand analysis and distribution plan — click Generate Now.</p>
              </div>
            </div>
            <button
              onClick={handleManualTrigger}
              disabled={isGenerating}
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 text-sm font-medium shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? 'Generating...' : 'Generate Now'}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'advisor' ? (
          <DebtAdvisor householdId={householdId} />
        ) : reports.length === 0 ? (
           <div className="h-full flex flex-col items-center justify-center text-slate-400 p-6">
             <Calendar className="w-12 h-12 mb-4 opacity-20" />
             <p className="font-semibold text-slate-600 mb-2">No Reports Yet</p>
             <p className="text-sm mt-2 text-slate-500 max-w-md text-center">
               The AI will wake up on the 1st of the month to review your past performance and strategically distribute your upcoming cash flow across fixed expenses, debts, goals, and your family wishlist!
             </p>
           </div>
        ) : (
          <div className="space-y-8">
            {reports.map((report) => (
              <div key={report.id} className="border border-indigo-100 bg-indigo-50/10 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-6 border-b border-indigo-100 pb-4">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-indigo-900">
                    Strategy Report: {format(new Date(report.year, report.month - 1), 'MMMM yyyy')}
                  </h3>
                </div>
                <div className="prose prose-sm prose-slate max-w-none prose-headings:text-slate-800 prose-a:text-indigo-600">
                  {report.content.split('\n').map((line, i) => {
                    if (line.startsWith('###')) return <h3 key={i} className="text-lg font-bold mt-6 mb-2">{line.replace('###', '')}</h3>;
                    if (line.startsWith('##')) return <h2 key={i} className="text-xl font-bold mt-8 mb-4 text-indigo-900 border-b pb-2">{line.replace('##', '')}</h2>;
                    if (line.startsWith('#')) return <h1 key={i} className="text-2xl font-bold mt-8 mb-4">{line.replace('#', '')}</h1>;
                    if (line.startsWith('-') || line.startsWith('*')) return <li key={i} className="ml-4">{line.replace(/^[-*]/, '')}</li>;
                    if (line.trim() === '') return <br key={i} />;
                    
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                      <p key={i} className="mb-2">
                        {parts.map((part, index) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={index}>{part.slice(2, -2)}</strong>;
                          }
                          return part;
                        })}
                      </p>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
